/**
 * Spending for a week, a month, a year or any dates: what was incurred (the
 * figure profit subtracts), what was actually paid out, and what is still to
 * pay — by category, location and vendor, and period by period.
 */

import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ExpenseSummary as Summary, ExpenseSummaryBucket } from '@cenox/shared';
import { api, qs } from '../../lib/api';
import { useAuth, useBusinessPath } from '../../lib/auth';
import { useLocationFilter, useLocationReady } from '../../lib/locationContext';
import { money, todayIn } from '../../lib/format';
import { Card, CardHeader, ErrorState, Input, Select } from '../../components/ui';

type Period = 'week' | 'month' | 'year' | 'custom';

const PERIODS: Array<{ value: Period; label: string }> = [
  { value: 'week', label: 'This week' },
  { value: 'month', label: 'This month' },
  { value: 'year', label: 'This year' },
  { value: 'custom', label: 'Custom dates' },
];

function addDays(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

/** The period's first day, and the bucket its series is drawn in. Weeks start on Monday. */
export function periodRange(period: Exclude<Period, 'custom'>, today: string): { from: string; bucket: ExpenseSummaryBucket } {
  if (period === 'year') return { from: `${today.slice(0, 4)}-01-01`, bucket: 'month' };
  if (period === 'month') return { from: `${today.slice(0, 7)}-01`, bucket: 'week' };
  const [y, m, d] = today.split('-').map(Number) as [number, number, number];
  const weekday = (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7; // Monday = 0
  return { from: addDays(today, -weekday), bucket: 'day' };
}

function bucketLabel(bucket: string, unit: ExpenseSummaryBucket): string {
  const at = new Date(`${bucket}T12:00:00Z`);
  if (unit === 'month') return at.toLocaleDateString(undefined, { month: 'short', year: 'numeric', timeZone: 'UTC' });
  const day = at.toLocaleDateString(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' });
  return unit === 'week' ? `Week of ${day}` : day;
}

export function ExpenseSummaryPanel(): ReactNode {
  const { business } = useAuth();
  const path = useBusinessPath();
  const today = todayIn(business?.timezone ?? 'UTC');
  const locationFilter = useLocationFilter();
  const locationReady = useLocationReady();

  const [period, setPeriod] = useState<Period>('month');
  const [customFrom, setCustomFrom] = useState(addDays(today, -29));
  const [customTo, setCustomTo] = useState(today);
  const [currency, setCurrency] = useState<string | undefined>(undefined);

  const range =
    period === 'custom'
      ? {
          from: customFrom,
          to: customTo,
          // A long custom range reads better by month, a short one by day.
          bucket: (customFrom <= addDays(customTo, -120) ? 'month' : customFrom <= addDays(customTo, -31) ? 'week' : 'day') as ExpenseSummaryBucket,
        }
      : { ...periodRange(period, today), to: today };
  const valid = Boolean(range.from && range.to && range.from <= range.to);
  const query = qs({ ...range, currencyCode: currency, ...locationFilter });

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['expenses', 'summary', business?.businessId, query],
    queryFn: async () => {
      const summary = await api.get<Summary>(path(`/expenses/summary${query}`));
      // A malformed answer is an error on this card, never a broken Expenses page.
      if (!Array.isArray(summary?.series) || !Array.isArray(summary?.byCategory)) throw new Error('Unexpected summary');
      return summary;
    },
    enabled: Boolean(business) && locationReady && valid,
  });

  const cur = data?.currencyCode ?? business?.currencyCode ?? 'NGN';

  return (
    <Card className="mb-4">
      <CardHeader
        title="Spending"
        subtitle="Costs are counted on the day they were incurred; paid is the money that actually left."
        action={
          <div className="flex flex-wrap items-end gap-2">
            <Select
              aria-label="Period"
              options={PERIODS}
              value={period}
              onChange={(e) => setPeriod(e.target.value as Period)}
            />
            {data && data.currencies.length > 0 ? (
              <Select
                aria-label="Currency"
                options={[data.currencyCode, ...data.currencies].map((c) => ({ value: c, label: c }))}
                value={data.currencyCode}
                onChange={(e) => setCurrency(e.target.value)}
              />
            ) : null}
          </div>
        }
      />
      {period === 'custom' ? (
        <div className="mb-4 grid grid-cols-2 gap-3 sm:max-w-md">
          <Input label="From" type="date" max={customTo} value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
          <Input label="To" type="date" min={customFrom} value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
        </div>
      ) : null}

      {!valid ? (
        <p className="text-sm text-slate-500">Choose a start date on or before the end date.</p>
      ) : error ? (
        <ErrorState message="Could not load spending." onRetry={() => refetch()} />
      ) : isPending ? (
        <div className="h-24 animate-pulse rounded-lg bg-slate-100" aria-label="Loading spending" />
      ) : (
        <div className="space-y-5">
          <dl className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Figure label="Expenses incurred" value={money(data.incurred, cur)} note={`${data.count} expense${data.count === 1 ? '' : 's'}`} />
            <Figure
              label="Paid out"
              value={money(data.paid, cur)}
              note={
                Number(data.returned ?? 0) > 0
                  ? `Money that left in the period; ${money(data.returned, cur)} came back from cancelled expenses`
                  : 'Money that left in the period'
              }
            />
            <Figure
              label="Still to pay"
              value={money(data.outstanding, cur)}
              note={`${data.outstandingCount} not fully paid, up to the period's end`}
              tone={Number(data.outstanding) > 0 ? 'warning' : undefined}
            />
          </dl>

          <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
            <Breakdown title="By category" lines={data.byCategory} currency={cur} total={data.incurred} />
            <Breakdown title="By location" lines={data.byBranch} currency={cur} total={data.incurred} />
            <Breakdown title="By vendor" lines={data.byVendor} currency={cur} total={data.incurred} />
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm" aria-label="Spending by period">
              <thead className="border-b border-slate-200 text-xs text-slate-600">
                <tr>
                  <th scope="col" className="py-2 text-left font-medium">Period</th>
                  <th scope="col" className="py-2 text-right font-medium">Incurred</th>
                  <th scope="col" className="py-2 text-right font-medium">Paid out</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.series.map((row) => (
                  <tr key={row.bucket}>
                    <td className="py-1.5 text-slate-700">{bucketLabel(row.bucket, range.bucket)}</td>
                    <td className="py-1.5 text-right tabular">{money(row.incurred, cur)}</td>
                    <td className="py-1.5 text-right tabular text-slate-600">{money(row.paid, cur)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Card>
  );
}

function Figure({ label, value, note, tone }: { label: string; value: string; note: string; tone?: 'warning' | undefined }): ReactNode {
  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <dt className="text-xs font-medium text-slate-500">{label}</dt>
      <dd className={`mt-1 text-lg font-semibold tabular ${tone === 'warning' ? 'text-amber-700' : 'text-slate-900'}`}>{value}</dd>
      <dd className="mt-0.5 text-xs text-slate-500">{note}</dd>
    </div>
  );
}

function Breakdown({
  title,
  lines,
  currency,
  total,
}: {
  title: string;
  lines: Summary['byCategory'];
  currency: string;
  total: string;
}): ReactNode {
  const whole = Number(total) || 1;
  return (
    <section aria-label={title}>
      <h3 className="mb-2 text-sm font-semibold text-slate-900">{title}</h3>
      {lines.length === 0 ? (
        <p className="text-sm text-slate-500">Nothing in this period.</p>
      ) : (
        <ul className="space-y-1.5 text-sm">
          {lines.slice(0, 8).map((line) => (
            <li key={`${line.key ?? ''}-${line.label}`} className="flex items-baseline justify-between gap-3">
              <span className="min-w-0 truncate text-slate-700">{line.label}</span>
              <span className="shrink-0 tabular text-slate-900">
                {money(line.amount, currency)}
                <span className="ml-1.5 text-xs text-slate-500">{Math.round((Number(line.amount) / whole) * 100)}%</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
